import { messagingSessionSchema } from '@secretario/shared';
import {
  ClientEvent,
  createClient,
  EventType,
  HttpApiEvent,
  IndexedDBStore,
  RoomEvent,
  SetPresence,
  type MatrixClient,
  type Room,
} from 'matrix-js-sdk';
import { useSyncExternalStore } from 'react';
import { apiFetch, ApiError } from '../api/http.js';
import { evaluateRecovery, recoveryCallbacks, resetRecovery } from './recovery.js';
import { getPrivacy, subscribePrivacy } from './privacy.js';
import { attachVerification, resetVerification } from './verification.js';
import { getDevice, readSession } from '../session.js';

/**
 * Cliente do mensageiro (Synapse fechado). A API do produto emite um token de
 * entrada; o homeserver cria o usuário Matrix no primeiro login. As credenciais
 * do homeserver ficam neste aparelho (uma por conta do produto) e a criptografia
 * de ponta a ponta (Rust/WASM) guarda as chaves em IndexedDB.
 */
export type MatrixStatus = 'idle' | 'starting' | 'ready' | 'off' | 'error';

type StoredCredentials = {
  productUserId: string;
  homeserverUrl: string;
  serverName: string;
  userId: string;
  deviceId: string;
  accessToken: string;
};

const CREDENTIALS_KEY = 'secretario.matrix';

let client: MatrixClient | null = null;
let status: MatrixStatus = 'idle';
let lastError = '';
let offline = false;
let starting: Promise<MatrixClient | null> | null = null;
const listeners = new Set<() => void>();

function setStatus(next: MatrixStatus, error = '') {
  status = next;
  lastError = error;
  for (const listener of listeners) listener();
}

function setOffline(next: boolean) {
  if (offline === next) return;
  offline = next;
  for (const listener of listeners) listener();
}

// Presença com privacidade por padrão: só fica "online" para os outros quem ligar em Ajustes.
function applyPresence(target: MatrixClient) {
  void target
    .setSyncPresence(getPrivacy().presence ? SetPresence.Online : SetPresence.Offline)
    .catch(() => {});
}
subscribePrivacy(() => {
  if (client) applyPresence(client);
});
if (typeof window !== 'undefined') {
  // A internet voltou: não espera o próximo ciclo para reconectar.
  window.addEventListener('online', () => client?.retryImmediately());
}

function readCredentials(): StoredCredentials | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS_KEY);
    const parsed = raw ? (JSON.parse(raw) as StoredCredentials) : null;
    const session = readSession();
    return parsed && session && parsed.productUserId === session.user.id ? parsed : null;
  } catch {
    return null;
  }
}

function writeCredentials(value: StoredCredentials | null) {
  try {
    if (value) localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(value));
    else localStorage.removeItem(CREDENTIALS_KEY);
  } catch {
    /* sem armazenamento: a sessão vive só em memória */
  }
}

async function login(): Promise<StoredCredentials> {
  const session = readSession();
  if (!session) throw new ApiError(401, 'invalid_session', 'Sessão inválida.');
  const grant = messagingSessionSchema.parse(
    await apiFetch<unknown>('/v1/messaging/session', { method: 'POST' }),
  );
  const temporary = createClient({ baseUrl: grant.homeserverUrl });
  const response = await temporary.loginRequest({
    type: 'org.matrix.login.jwt',
    token: grant.token,
    device_id: grant.deviceId,
    initial_device_display_name: getDevice().label,
  });
  const credentials: StoredCredentials = {
    productUserId: session.user.id,
    homeserverUrl: grant.homeserverUrl,
    serverName: grant.serverName,
    userId: response.user_id,
    deviceId: response.device_id,
    accessToken: response.access_token,
  };
  writeCredentials(credentials);
  return credentials;
}

/**
 * Bancos do aparelho (sincronização e chaves) são por aparelho. Se a conta do
 * produto troca de nome de usuário, o endereço no mensageiro muda e o mesmo
 * aparelho volta com outra conta: as chaves guardadas são da conta antiga e o SDK
 * recusa abrir ("the account in the store doesn't match"). Nesse caso a conta
 * nova ganha bancos próprios, marcados pelo endereço dela.
 */
const ACCOUNT_MISMATCH = /doesn't match the account in the constructor/;
const SEPARATE_STORES_KEY = 'secretario.matrix.bancosPorConta';

function storeSuffix(credentials: StoredCredentials): string {
  let separate: string | null;
  try {
    separate = window.localStorage.getItem(SEPARATE_STORES_KEY);
  } catch {
    separate = null;
  }
  return separate === credentials.userId
    ? `-${credentials.userId.replace(/[^a-z0-9]/gi, '_')}`
    : '';
}

async function start(): Promise<MatrixClient | null> {
  setStatus('starting');
  try {
    return await open();
  } catch (error) {
    const credentials = readCredentials();
    if (credentials && ACCOUNT_MISMATCH.test(String(error)) && !storeSuffix(credentials)) {
      try {
        window.localStorage.setItem(SEPARATE_STORES_KEY, credentials.userId);
      } catch {
        /* sem armazenamento local, a próxima abertura tenta de novo */
      }
      try {
        return await open();
      } catch (retry) {
        return failed(retry);
      }
    }
    return failed(error);
  } finally {
    starting = null;
  }
}

function failed(error: unknown): null {
  if (error instanceof ApiError && error.status === 503) {
    setStatus('off', error.message);
  } else {
    // O texto técnico fica no console; para a pessoa vai a frase que ajuda.
    console.warn('[mensageiro] não abriu:', error);
    setStatus('error', startErrorMessage(error));
  }
  return null;
}

/**
 * O endereço do mensageiro pode mudar (em 11/10 a origem passou a aceitar só a Cloudflare e o
 * endereço antigo parou de responder). Quem já tinha entrado troca para o atual sem entrar de
 * novo: o acesso é o mesmo servidor. Sem resposta rápida, segue com o guardado.
 */
async function currentHomeserver(credentials: StoredCredentials): Promise<StoredCredentials> {
  try {
    const answer = await Promise.race([
      apiFetch<{ homeserverUrl?: unknown }>('/v1/messaging/homeserver', { auth: false }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ]);
    const url = typeof answer?.homeserverUrl === 'string' ? answer.homeserverUrl : '';
    if (!/^https:\/\//.test(url) || url === credentials.homeserverUrl) return credentials;
    const next = { ...credentials, homeserverUrl: url };
    writeCredentials(next);
    return next;
  } catch {
    return credentials;
  }
}

async function open(): Promise<MatrixClient> {
  const saved = readCredentials();
  const credentials = saved ? await currentHomeserver(saved) : await login();
  const suffix = storeSuffix(credentials);
  const store = new IndexedDBStore({
    indexedDB: window.indexedDB,
    localStorage: window.localStorage,
    dbName: `secretario-sync-${credentials.deviceId}${suffix}`,
  });
  const next = createClient({
    baseUrl: credentials.homeserverUrl,
    accessToken: credentials.accessToken,
    userId: credentials.userId,
    deviceId: credentials.deviceId,
    store,
    useAuthorizationHeader: true,
    // O produto não tem chamadas: sem isso o SDK consulta microfone e câmera ao abrir.
    disableVoip: true,
    timelineSupport: true,
    cryptoCallbacks: recoveryCallbacks,
  });
  await next.store.startup();
  await next.initRustCrypto({
    useIndexedDB: true,
    cryptoDatabasePrefix: `secretario-crypto-${credentials.deviceId}${suffix}`,
  });
  attachVerification(next);

  // Servidor fechado: convite de outro usuário do produto entra direto.
  next.on(RoomEvent.MyMembership, (room: Room, membership: string) => {
    if (membership !== 'invite') return;
    const me = next.getUserId();
    const invite = me ? room.getMember(me)?.events.member : undefined;
    const inviter = invite?.getSender();
    const direct = invite?.getContent().is_direct === true;
    void next
      .joinRoom(room.roomId)
      .then(async () => {
        // Convite de conversa direta: registra do meu lado também (m.direct).
        if (!direct || !inviter) return;
        const current = (next.getAccountData(EventType.Direct)?.getContent() ?? {}) as Record<
          string,
          string[]
        >;
        if (current[inviter]?.includes(room.roomId)) return;
        await next.setAccountData(EventType.Direct, {
          ...current,
          [inviter]: [...(current[inviter] ?? []), room.roomId],
        });
      })
      .catch(() => {});
  });
  // Token invalidado (aparelho removido): esquece as credenciais e recomeça.
  next.on(HttpApiEvent.SessionLoggedOut, () => {
    writeCredentials(null);
    void resetMatrixClient();
  });
  let recoveryChecked = false;
  let everReady = false;
  next.on(ClientEvent.Sync, (state) => {
    // Depois de pronto, queda de rede não derruba a tela: vira um aviso de "sem conexão".
    if (state === 'ERROR' || state === 'RECONNECTING') {
      if (everReady) setOffline(true);
      // Nunca chegou a abrir: em vez do carregando sem fim, a frase e o "Tentar de novo"
      // (o SDK continua tentando; se conseguir, a lista aparece sozinha).
      else
        setStatus(
          'error',
          state === 'ERROR'
            ? 'Perdi a conexão com o mensageiro.'
            : 'Sem conexão com o mensageiro. Confira a internet e tente de novo.',
        );
    } else if (state === 'PREPARED' || state === 'SYNCING' || state === 'CATCHUP') {
      everReady = true;
      setOffline(false);
      setStatus('ready');
      if (!recoveryChecked) {
        recoveryChecked = true;
        void evaluateRecovery(next);
      }
    }
  });

  await next.startClient({ initialSyncLimit: 30 });
  // Só vale depois de iniciado (antes disso o SDK ignora): sem isso todo mundo
  // aparecia "online" mesmo com a opção desligada em Ajustes.
  applyPresence(next);
  client = next;
  void syncDisplayName(next);
  // Só em desenvolvimento: inspeção do cliente pelo console.
  if (import.meta.env.DEV)
    (window as { __secretarioMatrix?: MatrixClient }).__secretarioMatrix = next;
  return next;
}

/** O nome da conta é o que os contatos veem no mensageiro. */
async function syncDisplayName(target: MatrixClient): Promise<void> {
  const wanted = readSession()?.user.displayName?.trim();
  const userId = target.getUserId();
  if (!wanted || !userId) return;
  try {
    const current = (await target.getProfileInfo(userId, 'displayname')).displayname;
    if (current !== wanted) await target.setDisplayName(wanted);
  } catch {
    /* sem rede agora: tenta na próxima abertura */
  }
}

/** A frase que a pessoa lê quando o mensageiro não abre (nunca o erro técnico). */
export function startErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  const text = error instanceof Error ? error.message : String(error ?? '');
  if (/429|too many requests|limit_exceeded/i.test(text)) {
    return 'Foram muitas tentativas em pouco tempo. Espere um minuto e tente de novo.';
  }
  if (/failed to fetch|network|timeout|timed out|econn|connection/i.test(text)) {
    return 'Sem conexão com o mensageiro. Confira a internet e tente de novo.';
  }
  return 'O mensageiro não respondeu desta vez. Tente de novo em instantes.';
}

/** Nova tentativa depois de uma falha ao abrir (o que já estava guardado continua). */
export function retryMatrixClient(): void {
  if (starting) return;
  // Já aberto, mas sem a primeira sincronização: pede para tentar agora.
  if (client) {
    setStatus('starting');
    client.retryImmediately();
    return;
  }
  setStatus('idle');
}

export function ensureMatrixClient(): Promise<MatrixClient | null> {
  if (client) return Promise.resolve(client);
  if (!starting) starting = start();
  return starting;
}

export function getMatrixClient(): MatrixClient | null {
  return client;
}

export async function resetMatrixClient(): Promise<void> {
  const current = client;
  client = null;
  resetRecovery();
  resetVerification();
  setStatus('idle');
  if (current) {
    current.stopClient();
    await current.store.destroy().catch(() => {});
  }
}

/** Saída da conta: encerra o cliente e apaga as credenciais do homeserver deste aparelho. */
export async function forgetMatrixClient(): Promise<void> {
  writeCredentials(null);
  await resetMatrixClient();
}

export function getMatrixState(): { status: MatrixStatus; error: string; offline: boolean } {
  return { status, error: lastError, offline };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let snapshot: { status: MatrixStatus; error: string; offline: boolean } = {
  status,
  error: lastError,
  offline,
};
function getSnapshot() {
  if (snapshot.status !== status || snapshot.error !== lastError || snapshot.offline !== offline)
    snapshot = { status, error: lastError, offline };
  return snapshot;
}

export function useMatrixState() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
